import './lineItemsTableRow.scss';

import { ActionMenu, FocusEmitter, FocusTrap, TransparentButton } from '@approvalmax/ui';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clsx } from 'clsx';
import isEqual from 'lodash/isEqual';
import { DragLargeIcon, EllipsisIcon } from 'modules/sprites';
import { CSSProperties, FC, memo, useMemo } from 'react';
import bemFactory from 'react-bem-factory';

import { ColumnDefinition, LineItem } from '../../selectors/types/ColumnDefinition';
import { messages } from './LineItemsTableRow.messages';

interface LineItemsTableRowProps {
    className?: string;
    columnDefinitions: Array<ColumnDefinition<LineItem>>;
    lineItem: LineItem;
    lineItemIndex: number;
    canReorder?: boolean;
    onCloneLine?: (lineItemId: string) => void;
    onRemoveLine?: (lineItemId: string) => void;
    invalid?: boolean;
}

function getCellStyle(colDef: ColumnDefinition<LineItem>): CSSProperties | undefined {
    return colDef.flexGrow !== undefined
        ? {
              flexGrow: colDef.flexGrow,
          }
        : colDef.fixedWidth
          ? {
                flex: 'none',
                width: `${colDef.fixedWidth}px`,
            }
          : undefined;
}

const bem = bemFactory.block('reqf-line-items-table-row');
const qa = bemFactory.qa('reqf-line-items-table-row');

export const LineItemsTableRow: FC<LineItemsTableRowProps> = memo(
    (props) => {
        const {
            className,
            columnDefinitions,
            lineItem,
            lineItemIndex,
            onCloneLine,
            onRemoveLine,
            invalid: invalidTable,
            canReorder = true,
        } = props;

        const isCloneLineShown = onCloneLine && !lineItem.hideClone;
        const isRemoveLineShown = onRemoveLine && !lineItem.hideRemove;

        const { attributes, listeners, setNodeRef, transform, transition, active } = useSortable({
            id: lineItem.id,
        });

        const sortableStyles = useMemo<CSSProperties>(
            () => ({
                transform: CSS.Transform.toString(transform),
                transition,
            }),
            [transform, transition]
        );

        return (
            <div
                ref={setNodeRef}
                className={clsx(
                    bem.add(className)(),
                    active?.id === lineItem.id && 'reqf-line-items-table__active-dnd-line'
                )}
                data-qa={qa()}
                style={sortableStyles}
            >
                <div className={bem('row-before')}>
                    {canReorder && (
                        <div className={bem('drag-handle')} data-qa={qa('drag-handle')} {...attributes} {...listeners}>
                            <DragLargeIcon />
                        </div>
                    )}
                </div>

                <div className={bem('row-body')}>
                    {columnDefinitions.map((colDef) => {
                        const invalidCell = Boolean(colDef.invalid && colDef.invalid(lineItem));
                        const invalidCellText = invalidCell && colDef.invalidText && colDef.invalidText(lineItem);
                        const readonly = Boolean(colDef.readonly && colDef.readonly(lineItem));

                        return (
                            <FocusEmitter key={colDef.id}>
                                {({ focus }) => (
                                    <FocusTrap>
                                        {({ focused }) => (
                                            <div
                                                data-qa={qa('cell')}
                                                data-qa-id={colDef.id}
                                                data-qa-name={colDef.name}
                                                title={invalidCellText || undefined}
                                                className={bem('cell', {
                                                    right: colDef.alignRight,
                                                    focused,
                                                    readonly,
                                                    invalid: (invalidTable && !readonly) || invalidCell,
                                                })}
                                                style={getCellStyle(colDef)}
                                                onClick={focus}
                                            >
                                                {colDef.cell(
                                                    lineItem,
                                                    {
                                                        invalid: (invalidTable && !readonly) || invalidCell,
                                                        readonly,
                                                    },
                                                    lineItemIndex
                                                )}
                                            </div>
                                        )}
                                    </FocusTrap>
                                )}
                            </FocusEmitter>
                        );
                    })}
                </div>

                <div className={bem('row-after')}>
                    {(isCloneLineShown || isRemoveLineShown) && (
                        <ActionMenu
                            className={bem('line-action-menu')}
                            panelFlow='to-left'
                            button={
                                <TransparentButton
                                    qa={qa('line-action-button')}
                                    className={bem('line-action-button')}
                                    focusable={false}
                                >
                                    <EllipsisIcon width={21} height={5} />
                                </TransparentButton>
                            }
                        >
                            {isCloneLineShown && (
                                <ActionMenu.Item
                                    qa={qa('line-action-item-clone')}
                                    execute={() => onCloneLine(lineItem.id)}
                                    title={messages.lineActionCloneTitle}
                                >
                                    {messages.lineActionCloneText}
                                </ActionMenu.Item>
                            )}

                            {isRemoveLineShown && (
                                <ActionMenu.Item
                                    qa={qa('line-action-item-remove')}
                                    execute={() => onRemoveLine(lineItem.id)}
                                    title={messages.lineActionRemoveTitle}
                                >
                                    {messages.lineActionRemoveText}
                                </ActionMenu.Item>
                            )}
                        </ActionMenu>
                    )}
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => {
        if (
            prevProps.className !== nextProps.className ||
            prevProps.invalid !== nextProps.invalid ||
            prevProps.canReorder !== nextProps.canReorder ||
            prevProps.lineItemIndex !== nextProps.lineItemIndex ||
            prevProps.onCloneLine !== nextProps.onCloneLine ||
            prevProps.onRemoveLine !== nextProps.onRemoveLine
        ) {
            return false;
        }

        if (!isEqual(prevProps.lineItem, nextProps.lineItem)) {
            return false;
        }

        if (!isEqual(prevProps.columnDefinitions, nextProps.columnDefinitions)) {
            return false;
        }

        return true;
    }
);

LineItemsTableRow.displayName = 'LineItemsTableRow';
